/* eslint-disable max-len */
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { matchRoute, ROUTER_PRODUCT } from 'app/AppRouter';
import { RootState } from 'app/AppStore';
import { trackAppEvent } from 'app/AppTracker';
import { CopyToClipboardButton } from 'components/CopyToClipboardButton';
import ProductModal from 'components/ProductModal';
import QuantityBox from 'components/QuantityBox';
import QuantityModule from 'components/QuantityModule';
import { QuantityModuleUpdateComponentType } from 'components/QuantityModule/QuantityModule';
import StockDisplay from 'components/StockInfo';
import { isBulkCertasOil, getTechnicalCharacteristicsLabel } from 'components/StockInfo/catalogStockUtils';
import {
  addOtherSectionReferenceRequest,
  getBasketOtherSection,
  getBasketVehicles,
  updateReferenceQuantityRequest,
} from 'domains/basket/Basket.store';
import {
  addReferenceFromCatalogToCart,
  getLastVehicleDetail,
  removeCartReferenceInCatalog,
} from 'domains/catalog/Catalog.store';
import { RENATIVE_LABEL, RENATIVE_TECHNICAL_CHARACTERISTICS_CODE } from 'domains/catalog/Catalog.types';
import {
  addCrossSellOpened,
  getCrossSellOpened,
  saveCrossSellingReferenceSagaRequest,
} from 'domains/crossSelling/CrossSelling.store';
import { CrossSellingContextType } from 'domains/crossSelling/CrossSelling.types';
import { DHReferenceLocal, getPrice, ReferencePriceType } from 'domains/references';
import { useFetchFullTradingData } from 'domains/references/References.requests';
import { GarageView, getSparePartsView, getTokenUser } from 'domains/user';
import { useCrossSellReferenceDisplayed } from 'pages/CatalogPage/DH/SubcategorySection/SparePartsSection/ReferenceCardsContainer/PlateReferenceCard/CrossSellingSection/useCrossSellReferenceDisplayed';
import { BrandImage } from 'pages/CatalogPage/DH/SubcategorySection/SparePartsSection/ReferenceCardsContainer/ReferenceCard/BrandImage';
import { BlackButton, Box, Flex, MarginBox, ScalableImage, Text, WithTooltip, YellowCartArrowDownButton } from 'UI';
import { getData, textFormatter } from 'utils';
import {
  TRACKING_EVENT_ADD_CROSS_SELLING_TO_CART,
  TRACKING_EVENT_CART_PART_QUANTITY_ZERO,
  TRACKING_EVENT_PAGE_PRODUCT_PART_DELETION,
  TRACKING_EVENT_PAGE_RESULT_PART_DELETION,
} from 'utils/eventTracker/EventTracker.types';
import { ReferenceCardWrapper, SFlex } from './CrossSellingSection.styled';

const PRODUCT_IMAGE_SIZE = 210;
const CATALOG_IMAGE_SIZE = 82;

const useSaveCrossSellingReference = (
  referenceNumber: string,
  mainReferenceNumber: string,
  contextType: CrossSellingContextType,
) => {
  const dispatch = useDispatch();
  const tokenUser = useSelector(getTokenUser);
  const crossSellOpened = useSelector((state: RootState) => getCrossSellOpened(state, tokenUser?.id ?? ''));

  return React.useCallback(() => {
    const crossSellReferenceNumber = referenceNumber;
    const crossSellRefKey = `${crossSellReferenceNumber}::${contextType}`;
    if (tokenUser && !crossSellOpened.has(crossSellRefKey)) {
      dispatch(addCrossSellOpened({ userId: tokenUser.id, crossSellRef: crossSellReferenceNumber, contextType }));
      dispatch(
        saveCrossSellingReferenceSagaRequest({
          mainReferenceNumber,
          crossSellReferenceNumber,
          contextType,
          actionType: 'OPEN',
        }),
      );
    }
  }, [referenceNumber, mainReferenceNumber, contextType, tokenUser, crossSellOpened, dispatch]);
};

const Prices = ({ price, type }: { price: ReferencePriceType | undefined; type: 'catalog' | 'product' }) => {
  const { t } = useTranslation();
  const sparePartsView = useSelector(getSparePartsView);
  const isGarageView = sparePartsView === GarageView;

  if (type === 'catalog') {
    const priceBasic = isGarageView
      ? price?.garageView?.vatExcludedPrice
      : price?.clientView?.recommendedPriceVatIncluded;
    return (
      <Text type="base" bold>
        {textFormatter.formatCurrency(Number(priceBasic), price?.currency ?? '')}
      </Text>
    );
  }

  const priceFirstLine = isGarageView
    ? t('common.price.vat_exclude_price', '{{vat_exclude_price}} VAT. Excl', {
        vat_exclude_price: textFormatter.formatCurrency(
          Number(price?.garageView?.vatExcludedPrice),
          price?.currency ?? '',
        ),
      })
    : t('common.price.vat_include_price', '{{vat_include_price}} VAT. Incl', {
        vat_include_price: textFormatter.formatCurrency(
          Number(price?.clientView?.recommendedPriceVatIncluded),
          price?.currency ?? '',
        ),
      });
  const priceSecondLine = isGarageView
    ? t('common.price.or_vat_include_price', 'or {{vat_include_price}} VAT. Incl', {
        vat_include_price: textFormatter.formatCurrency(
          Number(price?.garageView?.vatIncludedPrice),
          price?.currency ?? '',
        ),
      })
    : t('common.price.or_vat_exclude_price', 'or {{vat_exclude_price}} VAT. Excl', {
        vat_exclude_price: textFormatter.formatCurrency(
          Number(price?.clientView?.recommendedPriceVatExcluded),
          price?.currency ?? '',
        ),
      });

  return (
    <>
      <Text type="base" bold>
        {priceFirstLine}
      </Text>
      <Text type={'light_dimmer'}>{priceSecondLine}</Text>
    </>
  );
};

const ProductName = ({ referenceName }: { referenceName?: string }) => (
  <WithTooltip
    placement={'topLeft'}
    title={
      <Text type={'dark_10_white'} transform={'sentence-case'} noWrap ellipsis>
        {referenceName ?? ''}
      </Text>
    }
  >
    <Text type={'light_10_black_65'} transform={'sentence-case'} noWrap ellipsis>
      {referenceName ?? ''}
    </Text>
  </WithTooltip>
);

const ReferenceNumber = ({
  vehicleKey,
  referenceNumber,
  mainReferenceNumber,
  contextType,
}: {
  vehicleKey?: string;
  referenceNumber: string;
  mainReferenceNumber: string;
  contextType: CrossSellingContextType;
}) => {
  const { t } = useTranslation();
  const handleSaveCrossSellingReference = useSaveCrossSellingReference(
    referenceNumber,
    mainReferenceNumber,
    contextType,
  );

  return (
    <Flex>
      <button
        onClick={handleSaveCrossSellingReference}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
        type="button"
      >
        <ProductModal
          triggerComponent={
            <Text type="sm" bold hoverUnderLine>
              <Trans i18nKey={'catalog.reference.referenceNumber.short'} tOptions={{ referenceNumber }}>
                Ref: {{ referenceNumber }}
              </Trans>
            </Text>
          }
          referenceNumber={referenceNumber}
          isVehicleCatalog={!!vehicleKey}
          isCrossSellReference={true}
        />
      </button>
      <MarginBox mr={5} />
      <CopyToClipboardButton
        textToCopy={referenceNumber}
        message={t(
          'catalog.reference_card.reference_number.copied_to_clipboard',
          'Reference number {{referenceNumber}} copied to clipboard',
          { referenceNumber },
        )}
      />
    </Flex>
  );
};

const CrossReferenceCard = ({
  mainReferenceNumber,
  reference,
  type,
}: {
  mainReferenceNumber: string;
  reference: DHReferenceLocal;
  type: 'catalog' | 'product';
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { referenceNumber, sellerNumber } = reference;
  const price = getData(useSelector((state: RootState) => getPrice(state, referenceNumber)));

  const vehicle = useSelector(getLastVehicleDetail);
  const vehicleKey = vehicle?.vehicleKey;
  const contextType: CrossSellingContextType = vehicleKey ? 'VEHICLE' : 'OTHER';
  const basketVehicles = useSelector(getBasketVehicles);
  const basketOtherSection = useSelector(getBasketOtherSection);
  const referencesOfCurrentVeh = basketVehicles.find((vehicle) => vehicle.vehicleDetail.vehicleKey === vehicleKey)
    ?.references;
  const referenceData = getData(reference);
  const renative =
    getTechnicalCharacteristicsLabel(referenceData, RENATIVE_TECHNICAL_CHARACTERISTICS_CODE) === RENATIVE_LABEL;
  useFetchFullTradingData(referenceNumber, vehicleKey);
  useCrossSellReferenceDisplayed(mainReferenceNumber, referenceNumber, contextType);

  const handleSaveCrossSellingReference = useSaveCrossSellingReference(
    referenceNumber,
    mainReferenceNumber,
    contextType,
  );

  const isApplicableOrInCurrVehCart = () => {
    if (vehicleKey) {
      return (
        reference.isApplicableToCurrentVehicle ||
        referencesOfCurrentVeh?.find((ref) => ref.referenceNumber === referenceNumber)
      );
    } else {
      return false;
    }
  };
  const getBasketReferences = () =>
    isApplicableOrInCurrVehCart() ? referencesOfCurrentVeh : basketOtherSection?.references;
  const basketReferences = getBasketReferences();
  const refQuantity = basketReferences?.find((ref) => ref.referenceNumber === referenceNumber)?.quantity as number;

  const handleAddToCart = () => {
    if (price?.garageView?.vatExcludedPrice && price.garageView?.vatIncludedPrice) {
      trackAppEvent(TRACKING_EVENT_ADD_CROSS_SELLING_TO_CART);
      if (vehicleKey && vehicle && reference.isApplicableToCurrentVehicle) {
        dispatch(
          addReferenceFromCatalogToCart({
            referenceNumber,
            isCrossSellReference: true,
            mainReferenceNumber,
            isFromProductPage: type === 'product',
            sellerNumber,
            sellerName: reference.sellerName,
          }),
        );
      } else {
        dispatch(
          addOtherSectionReferenceRequest({
            reference: {
              ...reference,
              isCrossSelling: true,
              mainReferenceNumber,
              isFromProductPage: type === 'product',
            },
          }),
        );
      }
    }
  };

  const handleQuantityZero = (componentType?: QuantityModuleUpdateComponentType) => {
    dispatch(
      removeCartReferenceInCatalog({
        basketReferenceType: isApplicableOrInCurrVehCart() ? 'VEHICLE' : 'OTHER',
        vehicleKey: isApplicableOrInCurrVehCart() ? vehicleKey : undefined,
        reference: { referenceNumber, sellerNumber },
        referenceType: reference.type,
        origin: undefined,
        supplierCode: undefined,
        referenceSource: 'STANDARD',
      }),
    );

    if (componentType === 'INPUT') {
      trackAppEvent(TRACKING_EVENT_CART_PART_QUANTITY_ZERO);
      return;
    }

    trackAppEvent(
      matchRoute(location.pathname, ROUTER_PRODUCT)
        ? TRACKING_EVENT_PAGE_PRODUCT_PART_DELETION
        : TRACKING_EVENT_PAGE_RESULT_PART_DELETION,
    );
  };

  function quantityCallback(quantity: number | string, componentType?: QuantityModuleUpdateComponentType) {
    if (quantity === 0) {
      handleQuantityZero(componentType);
    } else {
      dispatch(
        updateReferenceQuantityRequest({
          basketReferenceType: isApplicableOrInCurrVehCart() ? 'VEHICLE' : 'OTHER',
          vehicleKey: isApplicableOrInCurrVehCart() ? vehicleKey : undefined,
          externalBasketId: undefined,
          referenceToUpdate: { referenceNumber, sellerNumber },
          newQuantity: quantity as number,
          referenceType: reference.type,
          isBulkOilReference: isBulkCertasOil(reference.familyCode, reference.productModelCode, reference.packaging),
        }),
      );
    }
  }

  const renderImage = () => {
    const productImageKeys = reference && 'imageKeys' in reference ? reference?.imageKeys : undefined;
    const productImageUrl = productImageKeys && productImageKeys.length > 0 ? productImageKeys?.[0] : undefined;
    const catalogSource = reference?.source === 'dh' ? 'DATAHUB' : 'IAM';
    const size = type === 'product' ? PRODUCT_IMAGE_SIZE : CATALOG_IMAGE_SIZE;

    const imageComponent = (
      <ScalableImage
        imageUrl={productImageUrl ?? '_'}
        catalogSource={catalogSource}
        alt={`reference-${referenceNumber}`}
        cursor={'pointer'}
        fallbackSrc={''}
        width={size}
        height={size}
        fallbackComponent={
          <BrandImage
            referenceBrand={reference.brand}
            vehicleBrand={vehicle?.vehicleBrand ?? 'OTHER'}
            familyCode={reference.familyCode}
            renative={renative}
            size={size}
          />
        }
      />
    );

    return (
      <button
        onClick={handleSaveCrossSellingReference}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
        type="button"
      >
        <ProductModal
          triggerComponent={imageComponent}
          referenceNumber={referenceNumber}
          isVehicleCatalog={!!vehicleKey}
          isCrossSellReference={true}
        />
      </button>
    );
  };

  const renderStockDisplay = () => (
    <StockDisplay
      small={type === 'catalog'}
      isApplicableToCurrentVehicle={reference.isApplicableToCurrentVehicle ?? false}
      vehicleKey={vehicleKey}
      referenceNumber={referenceNumber}
      narrow={type === 'product'}
    />
  );

  const renderCartActions = () => {
    const isInBasket = basketReferences?.find((ref) => ref.referenceNumber === referenceNumber);
    switch (type) {
      case 'catalog':
        return (
          <>
            {isInBasket ? (
              <QuantityBox mini value={refQuantity} onChange={(quantity) => quantityCallback(quantity, 'INPUT')} />
            ) : (
              <YellowCartArrowDownButton onClick={handleAddToCart} />
            )}
          </>
        );
      case 'product':
      default:
        return (
          <>
            {isInBasket ? (
              <QuantityModule value={refQuantity} onChange={quantityCallback} showDelete />
            ) : (
              <BlackButton onClick={handleAddToCart}>{t('cart.action.add_to_cart', 'Add to cart')}</BlackButton>
            )}
          </>
        );
    }
  };

  const renderReferenceCard = () => {
    switch (type) {
      case 'catalog':
        return renderCatalogCrossSelling();
      case 'product':
        return renderProductCrossSelling();
    }
  };

  const renderCatalogCrossSelling = () => {
    return (
      <ReferenceCardWrapper type={'catalog'}>
        {renderImage()}
        <SFlex direction={'column'}>
          <ReferenceNumber
            vehicleKey={vehicleKey}
            referenceNumber={referenceNumber}
            mainReferenceNumber={mainReferenceNumber}
            contextType={contextType}
          />
          <ProductName referenceName={reference.name} />
          <Flex />
          <Flex direction={'row'} align={'center'}>
            {renderStockDisplay()}
            <Box width={5} />
            <Prices price={price} type={type} />
            <Flex direction={'row-reverse'}>{renderCartActions()}</Flex>
          </Flex>
        </SFlex>
      </ReferenceCardWrapper>
    );
  };

  const renderProductCrossSelling = () => {
    return (
      <ReferenceCardWrapper type={'product'}>
        <Flex direction={'column'} maxWidth={210}>
          {renderImage()}
          <Box height={15} />
          <ReferenceNumber
            vehicleKey={vehicleKey}
            referenceNumber={referenceNumber}
            mainReferenceNumber={mainReferenceNumber}
            contextType={contextType}
          />
          <ProductName referenceName={reference.name} />
          {renderStockDisplay()}
          <Prices price={price} type={type} />
          <Box height={15} />
          {renderCartActions()}
        </Flex>
      </ReferenceCardWrapper>
    );
  };

  return renderReferenceCard();
};

export default CrossReferenceCard;
